/**
 * HTTP helpers against the signaling server (same host as REACT_APP_SIGNALING_WS_URL).
 */
const WS_BASE = process.env.REACT_APP_SIGNALING_WS_URL || "";

export function signalingHttpBase(wsBase = WS_BASE) {
  if (!wsBase) throw new Error("REACT_APP_SIGNALING_WS_URL is not configured");
  const origin = /^wss?:\/\//i.test(wsBase) ? wsBase : `wss://${wsBase}`;
  return origin
    .replace(/^ws:/i, "http:")
    .replace(/^wss:/i, "https:")
    .replace(/\/$/, "");
}

async function parseJsonResponse(resp) {
  const text = await resp.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* ignore */
  }
  if (!resp.ok) {
    const detail = data.detail || data.error || `HTTP ${resp.status}`;
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data;
}

export async function fetchOrgQuota({ idToken, wsBase = WS_BASE }) {
  const resp = await fetch(`${signalingHttpBase(wsBase)}/v1/org/quota`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return parseJsonResponse(resp);
}

export async function fetchOrgMediaPolicy({ idToken, wsBase = WS_BASE }) {
  const resp = await fetch(`${signalingHttpBase(wsBase)}/v1/org/media-policy`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return parseJsonResponse(resp);
}

export async function updateOrgMediaPolicy({
  idToken,
  audioKbps,
  videoKbps,
  wsBase = WS_BASE,
}) {
  const body = {};
  if (audioKbps != null) body.audioKbps = Number(audioKbps);
  if (videoKbps != null) body.videoKbps = Number(videoKbps);
  const resp = await fetch(`${signalingHttpBase(wsBase)}/v1/org/media-policy`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return parseJsonResponse(resp);
}

export async function fetchInviteTicket({ idToken, roomId, ttlSeconds, wsBase = WS_BASE }) {
  const resp = await fetch(`${signalingHttpBase(wsBase)}/v1/invite-ticket`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ roomId, ttlSeconds }),
  });
  return parseJsonResponse(resp);
}

export async function validateInviteTicket({ ticket, wsBase = WS_BASE }) {
  const u = new URL(`${signalingHttpBase(wsBase)}/v1/invite/validate`);
  u.searchParams.set("ticket", ticket);
  const resp = await fetch(u.toString());
  return parseJsonResponse(resp);
}

/** Build a company-owned room id: companyId or companyId__slug */
export function buildRoomId(companyId, slug = "") {
  const c = (companyId || "").trim();
  const s = (slug || "").trim().replace(/[^A-Za-z0-9_-]/g, "");
  if (!c) return "";
  if (!s) return c;
  return `${c}__${s}`;
}
